import toMoneyFormat from '../util/to_money_format';

const handlers = {
  IncomingMessage:      getIncomingMessageText,
  OutgoingMessage:      getOutgoingMessageText,
  Transaction:          getTransactionText,
  Feedback:             getFeedbackText,
  FollowUpMessage:      getFollowUpMessageText,
  Ask:                  getAskText,
  Review:               getReviewText,
  Call:                 getCallText,
  InvoiceSent:          getInvoiceSentText,
  InvoicePaid:          getInvoicePaidText,
  InvoiceRefunded:      getInvoiceRefundedText,
  ConversationActivity: getConversationActivityText
};

const conversationActivityToVerb = {
  assign:   'assigned',
  unassign: 'unassigned'
};

export function getText({ activity, ...params }) {
  const { type } = activity;
  const handler  = handlers[type];

  if (handler)
    return handler({ activity, ...params });
  else
    return null;
}

function getCallText({ activity }) {
  const { call } = activity;
  if (call.direction === 'inbound')
    return 'Incoming Call';
  else
    return 'Outgoing Call';
}


function getConversationActivityText({ activity }) {
  const pastTenseVerb  = conversationActivityToVerb[activity?.conversationActivity?.verb];
  const subjectUserId  = activity?.conversationActivity?.subject?.user?.id;
  const subjectName    = activity?.conversationActivity?.subject?.user?.name ?? '';
  const objectUserId   = activity?.conversationActivity?.object?.user?.id;
  const objectUserName = activity?.conversationActivity?.object?.user?.name;
  const objectName     = objectUserId === subjectUserId ? 'themselves' : objectUserName;

  const textParts = [ subjectName, pastTenseVerb, objectName ];

  return textParts.every(Boolean) ? textParts.join(' ') : null;
}


export function getIncomingMessageText({ activity }) {
  if (activity.message.body)
    return `"${activity.message.body}"`;
  else {
    const { attachments } = activity.message;
    return getAttachmentTypeText(attachments);
  }
}


export function getOutgoingMessageText({ activity }) {
  if (activity.message.body)
    return `You: "${activity.message.body}"`;
  else {
    const { attachments } = activity.message;
    return getAttachmentTypeText(attachments);
  }
}

function getAttachmentTypeText(attachments = []) {
  const lastAttachment = attachments[attachments.length - 1];
  const mimeType       = lastAttachment.versions.original.mimeType;
  const isImage        = /image\/.*/.test(mimeType);

  return isImage ? 'Image' : 'Attachment';
}

export function getTransactionText({ activity }) {
  const { provider } = activity.transaction;
  if (provider)
    return `Last helped by ${provider.name}`;
  else
    return 'Last helped by your business';
}

export function getFeedbackText({ activity }) {
  const { feedback } = activity;
  const leftFeedback = !!feedback.message && !!feedback.message.body;

  if (leftFeedback)
    return `"${feedback.message.body}"`;

  if (feedback.promoter)
    return 'Would recommend';
  else
    return 'Would not recommend';
}

export function getFollowUpMessageText({ activity }) {
  const { message } = activity;
  const { ask }     = message;

  return getAskTextByType(ask);
}


export function getAskText({ activity }) {
  const { ask }  = activity;
  const isCustom = ask.service === 'custom';
  switch (ask.type) {
    case 'referral':
      return 'Would you share on Facebook?';
    default:
      if (isCustom)
        return 'Contact sent to custom site';
      else
        return `Contact sent to ${getServiceName(ask.service)}`;
  }
}

function getAskTextByType(ask) {
  if (!ask)
    return null;

  switch (ask.type) {
    case 'review': {
      const isCustom = ask.service === 'custom';
      if (isCustom)
        return 'Requested a review';
      else
        return `Requested a review on ${getServiceName(ask.service)}`;
    }
    case 'classify':
      return 'Requested a review';
    case 'referral':
      return 'Would you share on Facebook?';
    case 'serviceReminder':
      return 'Sent service reminder';
    case 'thankYou':
      return 'Thank you.';
    default:
      return 'Sent follow-up message';
  }
}

function getServiceName(service) {
  const services = {
    broadly:     'Broadly',
    facebook:    'Facebook',
    google:      'Google',
    nextdoor:    'Nextdoor',
    tripadvisor: 'TripAdvisor',
    yelp:        'Yelp'
  };

  return services[service] || service;
}


export function getReviewText({ activity }) {
  const { review } = activity;

  return `They wrote a review on ${getServiceName(review.service)}`;
}


export function getInvoicePaidText({ activity }) {
  const { invoice }   = activity;
  const { totalPaid } = invoice;

  return `Paid you ${toMoneyFormat({ amount: totalPaid })}`;
}


export function getInvoiceSentText({ activity }) {
  const { invoice } = activity;
  const { amount }  = invoice;

  return `Requested payment of ${toMoneyFormat({ amount })}`;
}


export function getInvoiceRefundedText({ activity }) {
  const { invoice }    = activity;
  const { refunds }    = invoice;
  const amountRefunded = refunds.reduce((acc, { amount }) => acc + parseFloat(amount), 0);


  return `Refunded a payment of ${toMoneyFormat({ amount: amountRefunded })}`;
}
